import React, { useCallback, useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { isValidYouTubeId } from '../../services/movieApi';
import { useAdminAuth } from '../AdminAuthContext';
import {
  deleteMoviesByVideoIds,
  fetchCategoriesAdmin,
  fetchDistinctChannelNames,
  fetchMovieCategoryIds,
  fetchMoviesAdmin,
  insertMovie,
  setMovieCategories,
  updateMovie
} from '../adminApi';

const emptyForm = {
  video_id: '',
  title: '',
  channel_name: '',
  published_date: '',
  description: '',
  view_count: 0,
  duration: '',
  embed_code: ''
};

export default function MoviesPage() {
  const { canEditMovies } = useAdminAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [channels, setChannels] = useState([]);
  const [sortField, setSortField] = useState('published_date');
  const [sortAsc, setSortAsc] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const res = await fetchMoviesAdmin({
      page,
      pageSize,
      search,
      channel: channelFilter,
      sortField,
      sortAsc
    });
    if (res.error) {
      setError(res.error.message || 'Failed to load movies');
      setRows([]);
      setTotal(0);
    } else {
      setRows(res.rows);
      setTotal(res.total);
    }
    setLoading(false);
  }, [page, pageSize, search, channelFilter, sortField, sortAsc]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    (async () => {
      const { channels: ch } = await fetchDistinctChannelNames();
      setChannels(ch || []);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const { rows } = await fetchCategoriesAdmin();
      setCategories(rows || []);
    })();
  }, []);

  const toggleAllPage = () => {
    if (selected.size === rows.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(rows.map((r) => r.video_id)));
    }
  };

  const toggleOne = (videoId) => {
    const next = new Set(selected);
    if (next.has(videoId)) next.delete(videoId);
    else next.add(videoId);
    setSelected(next);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setSelectedCategoryIds(new Set());
    setDialogOpen(true);
  };

  const openEdit = async (row) => {
    setEditingId(row.video_id);
    setForm({
      video_id: row.video_id || '',
      title: row.title || '',
      channel_name: row.channel_name || '',
      published_date: row.published_date
        ? String(row.published_date).slice(0, 10)
        : '',
      description: row.description || '',
      view_count: Number(row.view_count) || 0,
      duration: row.duration || '',
      embed_code: row.embed_code || ''
    });
    const { categoryIds } = await fetchMovieCategoryIds(row.video_id);
    setSelectedCategoryIds(new Set(categoryIds || []));
    setDialogOpen(true);
  };

  const saveMovie = async (e) => {
    e.preventDefault();
    if (!canEditMovies) return;
    if (!isValidYouTubeId(form.video_id.trim())) {
      alert('Invalid YouTube video ID (expect 11 characters).');
      return;
    }
    setSaving(true);
    const payload = {
      video_id: form.video_id.trim(),
      title: form.title.trim(),
      channel_name: form.channel_name.trim(),
      published_date: form.published_date
        ? new Date(form.published_date).toISOString()
        : new Date().toISOString(),
      description: form.description,
      view_count: parseInt(form.view_count, 10) || 0,
      duration: form.duration.trim(),
      embed_code: form.embed_code.trim()
    };

    let err;
    if (editingId) {
      const { error: e } = await updateMovie(editingId, {
        title: payload.title,
        channel_name: payload.channel_name,
        published_date: payload.published_date,
        description: payload.description,
        view_count: payload.view_count,
        duration: payload.duration,
        embed_code: payload.embed_code
      });
      err = e;
    } else {
      const { error: e } = await insertMovie(payload);
      err = e;
    }
    const videoIdForCategories = editingId || payload.video_id;
    if (!err && videoIdForCategories) {
      const { error: catErr } = await setMovieCategories(
        videoIdForCategories,
        [...selectedCategoryIds]
      );
      if (catErr) err = catErr;
    }
    setSaving(false);
    if (err) {
      alert(err.message || 'Save failed');
      return;
    }
    setDialogOpen(false);
    load();
  };

  const bulkDelete = async () => {
    if (!canEditMovies || selected.size === 0) return;
    if (
      !window.confirm(
        `Delete ${selected.size} movie(s)? This cannot be undone.`
      )
    ) {
      return;
    }
    const { error } = await deleteMoviesByVideoIds([...selected]);
    if (error) {
      alert(error.message || 'Delete failed');
      return;
    }
    setSelected(new Set());
    load();
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-white">Movies</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Manage rows in the{' '}
            <code className="text-zinc-400">movies</code> table (YouTube metadata).
          </p>
        </div>
        {canEditMovies && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-3 py-2 text-sm font-medium text-white hover:bg-violet-500"
            >
              <Plus className="h-4 w-4" />
              Add movie
            </button>
            <button
              type="button"
              onClick={bulkDelete}
              disabled={selected.size === 0}
              className="inline-flex items-center gap-2 rounded-lg border border-red-900/50 bg-red-950/30 px-3 py-2 text-sm text-red-300 hover:bg-red-950/50 disabled:opacity-40"
            >
              <Trash2 className="h-4 w-4" />
              Delete selected ({selected.size})
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-3 flex-wrap">
        <input
          type="search"
          placeholder="Search title, description, channel…"
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
          className="flex-1 min-w-[200px] rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-white"
        />
        <select
          value={channelFilter}
          onChange={(e) => {
            setPage(1);
            setChannelFilter(e.target.value);
          }}
          className="rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-white"
        >
          <option value="">All channels</option>
          {channels.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          value={sortField}
          onChange={(e) => setSortField(e.target.value)}
          className="rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-white"
        >
          <option value="published_date">Sort: published</option>
          <option value="created_at">Sort: created (if column exists)</option>
          <option value="title">Sort: title</option>
          <option value="view_count">Sort: views</option>
          <option value="channel_name">Sort: channel</option>
        </select>
        <button
          type="button"
          onClick={() => setSortAsc((a) => !a)}
          className="rounded-lg border border-zinc-700 px-3 py-2 text-sm text-zinc-300"
        >
          {sortAsc ? 'Ascending' : 'Descending'}
        </button>
      </div>

      {error && (
        <div className="text-sm text-red-400">{error}</div>
      )}

      <div className="rounded-xl border border-zinc-800 overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="bg-zinc-900/80 text-zinc-400 text-xs uppercase">
            <tr>
              {canEditMovies && (
                <th className="px-3 py-2 w-10">
                  <input
                    type="checkbox"
                    checked={
                      rows.length > 0 && selected.size === rows.length
                    }
                    onChange={toggleAllPage}
                  />
                </th>
              )}
              <th className="px-3 py-2">Video ID</th>
              <th className="px-3 py-2">Title</th>
              <th className="px-3 py-2">Channel</th>
              <th className="px-3 py-2">Views</th>
              <th className="px-3 py-2">Published</th>
              {canEditMovies && <th className="px-3 py-2 w-24" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {loading ? (
              <tr>
                <td
                  colSpan={canEditMovies ? 7 : 5}
                  className="px-3 py-8 text-center text-zinc-500"
                >
                  Loading…
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.video_id} className="hover:bg-zinc-900/50">
                  {canEditMovies && (
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selected.has(r.video_id)}
                        onChange={() => toggleOne(r.video_id)}
                      />
                    </td>
                  )}
                  <td className="px-3 py-2 font-mono text-xs text-zinc-400">
                    {r.video_id}
                  </td>
                  <td className="px-3 py-2 text-white max-w-xs truncate">
                    {r.title}
                  </td>
                  <td className="px-3 py-2 text-zinc-400">{r.channel_name}</td>
                  <td className="px-3 py-2 tabular-nums">
                    {Number(r.view_count).toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-zinc-500">
                    {r.published_date
                      ? String(r.published_date).slice(0, 10)
                      : '—'}
                  </td>
                  {canEditMovies && (
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => openEdit(r)}
                        className="p-1.5 rounded text-zinc-400 hover:text-white hover:bg-zinc-800"
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td
                  colSpan={canEditMovies ? 7 : 5}
                  className="px-3 py-8 text-center text-zinc-500"
                >
                  No movies match your filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-zinc-500">
        <span>
          Page {page} of {totalPages} · {total.toLocaleString()} total
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded border border-zinc-700 px-3 py-1 disabled:opacity-30"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="rounded border border-zinc-700 px-3 py-1 disabled:opacity-30"
          >
            Next
          </button>
        </div>
      </div>

      <Dialog.Root open={dialogOpen} onOpenChange={setDialogOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/70 z-50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(100vw-2rem,520px)] max-h-[90vh] overflow-y-auto -translate-x-1/2 -translate-y-1/2 rounded-xl border border-zinc-700 bg-zinc-900 p-6 shadow-xl focus:outline-none">
            <Dialog.Title className="text-lg font-semibold text-white mb-4">
              {editingId ? 'Edit movie' : 'Add movie'}
            </Dialog.Title>
            <form onSubmit={saveMovie} className="space-y-3">
              <div>
                <label className="text-xs text-zinc-500">Video ID</label>
                <input
                  className="mt-1 w-full rounded border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm text-white font-mono disabled:opacity-50"
                  value={form.video_id}
                  disabled={!!editingId}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, video_id: e.target.value }))
                  }
                  required
                />
              </div>
              <div>
                <label className="text-xs text-zinc-500">Title</label>
                <input
                  className="mt-1 w-full rounded border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm text-white"
                  value={form.title}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, title: e.target.value }))
                  }
                  required
                />
              </div>
              <div>
                <label className="text-xs text-zinc-500">Channel</label>
                <input
                  className="mt-1 w-full rounded border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm text-white"
                  value={form.channel_name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, channel_name: e.target.value }))
                  }
                  required
                />
              </div>
              <div>
                <label className="text-xs text-zinc-500">Published date</label>
                <input
                  type="date"
                  className="mt-1 w-full rounded border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm text-white"
                  value={form.published_date}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, published_date: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="text-xs text-zinc-500">View count</label>
                <input
                  type="number"
                  min={0}
                  className="mt-1 w-full rounded border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm text-white"
                  value={form.view_count}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      view_count: parseInt(e.target.value, 10) || 0
                    }))
                  }
                />
              </div>
              <div>
                <label className="text-xs text-zinc-500">Duration</label>
                <input
                  className="mt-1 w-full rounded border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm text-white"
                  value={form.duration}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, duration: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="text-xs text-zinc-500">Embed code</label>
                <textarea
                  className="mt-1 w-full rounded border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm text-white font-mono min-h-[80px]"
                  value={form.embed_code}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, embed_code: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="text-xs text-zinc-500">Description</label>
                <textarea
                  className="mt-1 w-full rounded border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm text-white min-h-[100px]"
                  value={form.description}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, description: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="text-xs text-zinc-500">Collections</label>
                <div className="mt-2 max-h-40 overflow-y-auto rounded border border-zinc-800 bg-zinc-950/80 px-2 py-2 space-y-1.5">
                  {categories.length === 0 ? (
                    <p className="text-xs text-zinc-500">
                      No collections yet — add them under Collections.
                    </p>
                  ) : (
                    categories.map((c) => {
                      const id = c.id;
                      const checked = selectedCategoryIds.has(id);
                      return (
                        <label
                          key={id}
                          className="flex items-center gap-2 text-sm text-zinc-200 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            className="rounded border-zinc-600"
                            checked={checked}
                            disabled={!canEditMovies}
                            onChange={() => {
                              setSelectedCategoryIds((prev) => {
                                const next = new Set(prev);
                                if (next.has(id)) next.delete(id);
                                else next.add(id);
                                return next;
                              });
                            }}
                          />
                          <span
                            className="inline-block h-3 w-3 rounded-sm border border-zinc-600 shrink-0"
                            style={{ backgroundColor: c.color || '#6366f1' }}
                          />
                          <span className="truncate">{c.name}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Dialog.Close asChild>
                  <button
                    type="button"
                    className="rounded-lg px-3 py-2 text-sm text-zinc-400 hover:bg-zinc-800"
                  >
                    Cancel
                  </button>
                </Dialog.Close>
                <button
                  type="submit"
                  disabled={saving || !canEditMovies}
                  className="rounded-lg bg-violet-600 px-4 py-2 text-sm text-white hover:bg-violet-500 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
